'use client';

import type { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ExploreViewType from '@alltrails/amplitude/enums/ExploreViewType';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import logExploreMapViewViewed from '@alltrails/amplitude/events/logExploreMapViewViewed';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Toast from '@alltrails/denali/components/Toast';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useDistanceAway from '@alltrails/explore-service/hooks/useDistanceAway';
import useExploreServiceResults from '@alltrails/explore-service/hooks/useExploreServiceResults';
import { sortParamKey } from '@alltrails/filters/utils/exploreSearchParams';
import useMap from '@alltrails/maps/hooks/useMap';
import { getMapOverlay } from '@alltrails/maps/utils/amplitudeHelpers';
import { collapsedTrailPinsId, regularTrailPinsId } from '@alltrails/maps/utils/layers';
import useExploreSearchParams from '@/hooks/useExploreSearchParams';
import useExploreServiceMapMove from '@/hooks/useExploreServiceMapMove';
import useInitialMapPosition from '@/hooks/useInitialMapPosition';
import { useResetState } from '@/hooks/useResetState';
import useShouldFetchParks from '@/hooks/useShouldFetchParks';
import useSprigEvent from '@/hooks/useSprigEvent';
import useUpdateMapResults from '@/hooks/useUpdateMapResults';
import { useLogExploreResultsReturned } from '@/utils/analyticsHelpers';
import { serializeFiltersToSearchParams } from '@/utils/exploreFilterParams';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setExploreViewType } from '@/utils/slices/exploreSlice';

type ExploreRootProps = { userLocation?: { lat: number; lng: number }; searchParams: ParsedUrlQuery };

const ExploreRoot = ({ userLocation, searchParams }: ExploreRootProps) => {
  const router = useRouter();
  const map = useMap();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileSizedScreen();
  const { hasPermission } = useAuthorization();
  const hasTrailsManagePermission = hasPermission({ permission: 'trails:manage' });
  const experiments = useExperiments();
  const debugEnabled = experiments['mugen-explore-debug']?.value === 'on';
  const isTrailheadsExploreEnabled = experiments['web-disco-rm-trailheads-explore']?.value === 'on';
  const {
    initialAdminFilters,
    initialDistanceAway,
    initialFilters,
    initialNaturalLanguageSearchData,
    initialSeasonalMonth,
    initialSort,
    initialTrailEngagement
  } = useExploreSearchParams();
  const { dynamicStyleIds, isShowingMobileList, panelCollapsed, renderedPOIs, staticStyleIds, searchResultsSummary, naturalLanguageQuery } =
    useAppSelector(state => ({
      dynamicStyleIds: state.map.dynamicStyleIds,
      isShowingMobileList: state.explore.isShowingMobileList,
      panelCollapsed: state.explore.panelCollapsed,
      renderedPOIs: state.map.renderedPOIs,
      staticStyleIds: state.map.staticStyleIds,
      searchResultsSummary: state.exploreService.searchResultsSummary,
      naturalLanguageQuery: state.exploreService.naturalLanguageQuery
    }));
  const shouldFetchParks = useShouldFetchParks();
  const isPhotospotOverlaySelected = dynamicStyleIds?.includes('photospot') && hasTrailsManagePermission;

  const useExploreServiceResultsArgs = useMemo(
    () => ({ hasTrails: true, isPhotospotOverlaySelected, shouldFetchParks }),
    [shouldFetchParks, isPhotospotOverlaySelected]
  );
  const { exploreData } = useExploreServiceResults(map, useExploreServiceResultsArgs);
  useUpdateMapResults();
  useExploreServiceMapMove(debugEnabled);

  // When the NLS response identifies a park, redirect to the explore park page & carry the filters and sort along in
  // the URL so the park page can seed them. NLS params are deliberately NOT carried: the park page reads NLS context
  // (query, summary, analytic data) from redux to avoid re-firing redirects if the user returns to `/explore`.
  const languageRegionCode = useLanguageRegionCode();
  const nlsParkRedirectEnabled = experiments['web-discover-rm-nls-improvements']?.value === 'treatment';
  useEffect(() => {
    if (!nlsParkRedirectEnabled || !naturalLanguageQuery) {
      return;
    }

    const place = exploreData?.place;
    if (!place || place.type !== 'area') {
      return;
    }

    const params = new URLSearchParams();
    serializeFiltersToSearchParams(exploreData?.searchConfig?.filters, params);
    if (exploreData?.searchConfig?.sort) {
      params.append(sortParamKey, exploreData.searchConfig.sort);
    }

    const query = params.toString();
    router.replace(wrapUrlSafe(`/explore/${place.slug}${query ? `?${query}` : ''}`, languageRegionCode));
  }, [exploreData?.place, exploreData?.searchConfig, languageRegionCode, naturalLanguageQuery, nlsParkRedirectEnabled, router]);

  // Send any pending sprig event before resetting the state
  useSprigEvent();

  const resetState = useMemo<Parameters<typeof useResetState>[0]>(
    () => ({
      persistKeys: {
        conditions: ['activeCondition', 'preloadedOverlayUTCs', 'selectedUTC'],
        explore: ['hasNavigatedClientSide', 'initialMapBounds', 'initialMapCenter', 'isReturningToExplore', 'mapPadding', 'returningBounds'],
        exploreService: ['boundingBox', 'locationResult', 'mapRotation', 'searchResults', 'searchResultsSummary'],
        exploreCustomRoute: ['sprigEvent']
      },
      initialState: {
        explore: { showFilterBar: true },
        exploreService: {
          adminFilters: initialAdminFilters,
          distanceAway: initialDistanceAway,
          filters: initialFilters,
          naturalLanguageQuery: initialNaturalLanguageSearchData?.query,
          naturalLanguageSearchAnalyticData: initialNaturalLanguageSearchData?.analyticsData,
          seasonalMonth: initialSeasonalMonth,
          skipQuery: true, // skip initial query to prevent flickering results when transitioning to/from other explore routes. This should be set to false on the first moveEnd event.
          sort: initialSort,
          trailEngagement: initialTrailEngagement,
          userLocation
        },
        // Trail pins are replaced by trailhead pins in the trailheads experiment. Applied through the reset rather than
        // a separate effect so the reset can't clear the flag when returning to /explore.
        maps: { hideTrailPins: isTrailheadsExploreEnabled }
      }
    }),
    [
      initialAdminFilters,
      initialDistanceAway,
      initialFilters,
      initialNaturalLanguageSearchData,
      initialSeasonalMonth,
      initialSort,
      initialTrailEngagement,
      isTrailheadsExploreEnabled,
      userLocation
    ]
  );
  useResetState(resetState);
  useInitialMapPosition({
    hasInitialNaturalLanguageQuery: Boolean(initialNaturalLanguageSearchData?.query),
    searchParams,
    userLocation
  });

  const [hasLoggedMapViewEvent, setHasLoggedMapViewEvent] = useState(false);
  useEffect(() => {
    // Log the event only once and wait for results to come back before logging
    if (!hasLoggedMapViewEvent && map && searchResultsSummary && renderedPOIs !== undefined) {
      const logEvent = () => {
        // Check if the trail pins layer exists before querying
        // This prevents "feature index out of bounds" errors when the layer hasn't rendered yet
        if (!map.getLayer(regularTrailPinsId)) {
          if (isTrailheadsExploreEnabled) {
            // We are in the trailheads explore experiment,
            // but we need to log the event even if the trail pins layer doesn't exist
            // TODO https://alltrails.atlassian.net/browse/MAPS-204
            // Add num_closed_pins_viewed, num_completed_pins_viewed, num_saved_pins_viewed
            const { lat, lng } = map.getCenter();
            logExploreMapViewViewed({
              lat,
              lng,
              num_closed_pins_viewed: 0,
              num_completed_pins_viewed: 0,
              num_pois: renderedPOIs?.count,
              num_results_returned: searchResultsSummary.count,
              num_saved_pins_viewed: 0,
              zoom_level: map.getZoom(),
              heatmap_turned_on: dynamicStyleIds?.includes('newHeatmap'),
              overlays_applied: [...dynamicStyleIds, ...staticStyleIds]?.map(id => getMapOverlay(id)) as MapOverlay[]
            });
            setHasLoggedMapViewEvent(true);
            return;
          } else {
            return;
          }
        }

        const { lat, lng } = map.getCenter();

        let numClosedPins = 0;
        let numSavedPins = 0;
        let numCompletedPins = 0;

        try {
          // Query rendered trail pins to count visible pins with interaction properties
          // Note: Clusters are rendered on a separate layer (trailClustersId) and won't appear here
          // Individual pins are counted based on their properties (isClosed, isSaved, isCompleted)
          const trailPins = map.queryRenderedFeatures({ layers: [regularTrailPinsId, collapsedTrailPinsId] });

          // Count individual trail pins based on their interaction properties
          trailPins.forEach(feature => {
            const props = feature.properties;
            // Skip clusters if they somehow appear (they have clusterTotalCount property)
            if (props?.clusterTotalCount) {
              return;
            }

            if (props?.isClosed === true) {
              numClosedPins += 1;
            } else if (props?.isSaved === true) {
              numSavedPins += 1;
            } else if (props?.isCompleted === true) {
              numCompletedPins += 1;
            }
          });
        } catch {
          // If queryRenderedFeatures fails (e.g., layer not fully initialized), default to 0
          // This prevents crashes and ensures the event still logs with zero counts
          // Silently handle this race condition since we already check layer existence above
        }
        logExploreMapViewViewed({
          lat,
          lng,
          num_closed_pins_viewed: numClosedPins,
          num_completed_pins_viewed: numCompletedPins,
          num_pois: renderedPOIs?.count,
          num_results_returned: searchResultsSummary.count,
          num_saved_pins_viewed: numSavedPins,
          zoom_level: map.getZoom(),
          heatmap_turned_on: dynamicStyleIds?.includes('newHeatmap'),
          overlays_applied: [...dynamicStyleIds, ...staticStyleIds]?.map(id => getMapOverlay(id)) as MapOverlay[]
        });
        setHasLoggedMapViewEvent(true);
      };

      // Wait for map to be idle (finished rendering) before querying features
      // This ensures the trail pins layer is fully rendered and ready to query
      if (map.idle()) {
        logEvent();
        return undefined;
      }

      const onIdle = () => {
        logEvent();
        map.off('idle', onIdle);
      };
      map.once('idle', onIdle);
      return () => {
        map.off('idle', onIdle);
      };
    }
    return undefined;
  }, [dynamicStyleIds, staticStyleIds, hasLoggedMapViewEvent, isTrailheadsExploreEnabled, map, searchResultsSummary, renderedPOIs]);

  useEffect(() => {
    // Retrigger the event if the overlays change
    setHasLoggedMapViewEvent(false);
  }, [dynamicStyleIds, staticStyleIds]);

  useLogExploreResultsReturned();

  useEffect(() => {
    let exploreViewType: ExploreViewType = 'map';
    if (isMobile) {
      exploreViewType = isShowingMobileList ? 'list' : 'map';
    } else {
      exploreViewType = panelCollapsed ? 'map' : 'split view';
    }
    dispatch(setExploreViewType(exploreViewType));
  }, [dispatch, isMobile, isShowingMobileList, panelCollapsed]);

  const [isDistanceAwayErrorToastOpen, setIsDistanceAwayErrorToastOpen] = useState(false);
  const onDistanceAwayError = useCallback(() => {
    setIsDistanceAwayErrorToastOpen(true);
  }, []);
  const distanceAwayErrorToast = useMemo(
    () => (
      // Render in a modal portal so the toast can show over the filter modal
      <ModalPortal>
        <Toast
          isOpen={isDistanceAwayErrorToastOpen}
          message={<FormattedMessage defaultMessage="We can’t calculate driving distance from this location" />}
          position="alternative"
          setIsOpen={setIsDistanceAwayErrorToastOpen}
          type="error"
        />
      </ModalPortal>
    ),
    [isDistanceAwayErrorToastOpen]
  );
  useDistanceAway(map, onDistanceAwayError);

  return <>{distanceAwayErrorToast}</>;
};

export default ExploreRoot;
